// src/SweetXoi/SweetXoi.jsx
import React from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Box, Grid, Typography } from "@mui/material";
import Footer from "../Components/Footer/Footer";
import useMetaTracking from "../hooks/useMetaTracking";

// Hero image (also used for social thumbnail)
import HeroImg from "../Assets/sweet_xoi.png";

// Sweet Xôi images
import XoiPandan from "../Assets/xoi_pandan.png";
import XoiRed from "../Assets/xoi_red.png";
import XoiCorn from "../Assets/xoi_corn.png";
import XoiMagenta from "../Assets/xoi_magenta.png";
import XoiPeanut from "../Assets/xoi_peanut.png";
import XoiDurian from "../Assets/xoi_durian.png";

function SweetXoi() {
  useMetaTracking("ViewContent", {
    content_name: "Sweet Xoi",
    content_category: "Vegan Xoi",
  });

  const BASE_URL = "https://www.donghungvienbakery.com";
  const PAGE_PATH = "/sweet-xoi";

  return (
    <HelmetProvider>
      <Helmet>
        <title>Sweet Xôi | Đông Hưng Viên Bakery</title>
        <link rel="canonical" href={`${BASE_URL}${PAGE_PATH}`} />

        {/* Thumbnail for social preview */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${BASE_URL}${PAGE_PATH}`} />
        <meta property="og:title" content="Sweet Xôi | Đông Hưng Viên Bakery" />
        <meta
          property="og:description"
          content="Xôi (pronounced s-oy) is a classic Vietnamese comfort food. It's glutinous rice steamed for a perfectly tender and chewy texture—sweet, fragrant, and comforting in every bite."
        />
        <meta property="og:image" content={`${BASE_URL}${HeroImg}`} />
        <meta property="og:image:alt" content="Sweet Xôi at Đông Hưng Viên Bakery" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Sweet Xôi | Đông Hưng Viên Bakery" />
        <meta
          name="twitter:description"
          content="Xôi (pronounced s-oy) is a classic Vietnamese comfort food. It's glutinous rice steamed for a perfectly tender and chewy texture—sweet, fragrant, and comforting in every bite."
        />
        <meta name="twitter:image" content={`${BASE_URL}${HeroImg}`} />
      </Helmet>

      <Box className="product-page" sx={{ pt: 10, background: "#0f0f0f" }}>
        {/* Header / Intro Section */}
        <Box sx={{ py: 4 }}>
          <Box sx={{ maxWidth: 1240, width: "82%", mx: "auto", px: { xs: 2, md: 0 } }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", justifyContent: { xs: "center", md: "flex-end" } }}>
                  <Box
                    component="img"
                    src={HeroImg}
                    alt="Sweet Xôi"
                    sx={{
                      width: { xs: "90%", md: "100%" },
                      maxHeight: "400px",
                      objectFit: "cover",
                      borderRadius: "16px",
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2 }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: "bold",
                      mb: 1,
                      fontSize: { xs: "24px", sm: "30px", md: "35px" },
                      textTransform: "uppercase",
                      color: "#fff",
                    }}
                  >
                    Sweet Xôi
                  </Typography>

                  <Box sx={{ borderBottom: "1px solid #fff", mt: 2 }} />

                  <Typography
                    variant="body1"
                    sx={{
                      mt: 4,
                      mb: 2,
                      lineHeight: 1.6,
                      fontSize: { xs: "15px", md: "16px" },
                      color: "#fff",
                    }}
                  >
                    Xôi (pronounced s-oy) is a classic Vietnamese comfort food. It's glutinous rice that gets steamed
                    for a perfectly tender and chewy texture (never mushy!). As a super versatile food, it can be
                    enjoyed as a sweet snack or a hearty meal any time of day.
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      mb: 2.5,
                      lineHeight: 1.6,
                      fontSize: { xs: "15px", md: "16px" },
                      color: "#fff",
                    }}
                  >
                    <strong>Optional:</strong> Add sesame salt for a nutty, salty crunch that perfectly balances the
                    sweetness.
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      mb: 1.5,
                      lineHeight: 1.6,
                      fontSize: { xs: "18px", md: "20px" },
                      color: "#fff",
                      fontWeight: 700,
                    }}
                  >
                    Price: $3.00
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "#fff",
                      fontStyle: "italic",
                      fontSize: { xs: "14px", md: "15px" },
                    }}
                  >
                    Vegan, Gluten-Free
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ borderBottom: "1px solid #fff", maxWidth: 1240, width: "82%", mx: "auto" }} />

        {/* Sweet Xôi Grid */}
        <Box sx={{ py: 6 }}>
          <Box sx={{ maxWidth: 1240, width: "82%", mx: "auto", px: { xs: 2, md: 0 } }}>
            <Grid container spacing={4} justifyContent="center">
              {[
                { img: XoiPandan, title: "Pandan Sticky Rice", url: "/xoi-pandan" },
                { img: XoiRed, title: "Sweet Red Sticky Rice", url: "/xoi-red" },
                { img: XoiCorn, title: "Corn Sticky Rice", url: "/xoi-corn" },
                { img: XoiMagenta, title: "Magenta Leaves Sticky Rice", url: "/xoi-magenta-leaves" },
                { img: XoiPeanut, title: "Peanut Sticky Rice", url: "/xoi-peanut" },
                { img: XoiDurian, title: "Durian Sticky Rice", url: "/xoi-durian" },
              ].map((xoi, i) => (
                <Grid item xs={10} sm={4} md={3.5} key={i}>
                  <Box sx={{ textAlign: "center" }}>
                    <a
                      href={`${BASE_URL}${xoi.url}`}
                      style={{ textDecoration: "none" }}
                      rel="noopener noreferrer"
                    >
                      <Box
                        component="img"
                        src={xoi.img}
                        alt={xoi.title}
                        sx={{
                          width: "100%",
                          borderRadius: "30px",
                          aspectRatio: "1 / 1",
                          objectFit: "cover",
                        }}
                      />
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mt: 1.5,
                          color: "#fff",
                          letterSpacing: 0.4,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          fontSize: { xs: "12px", md: "13px" },
                          display: "inline-block",
                        }}
                      >
                        {xoi.title}
                      </Typography>
                    </a>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        <Footer />
      </Box>
    </HelmetProvider>
  );
}

export default SweetXoi;